import { useMutation } from "@apollo/client/react"
import cn from "classnames"
import { debounce } from "lodash-es"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { graphql } from "@/api"
import ClientData from "@/common/client_data"
import MediaQuery from "@/common/components/media_query"
import env from "@/common/env"
import tailwindTheme from "@/common/styles/theme.module.css"
import type Sign from "@/signomatic/app/sign"
import RowButton from "@/signomatic/components/row_button"
import { copyToClipboard, ios } from "@/signomatic/util"
import { info } from "@/signomatic/util/logger"
import { ShareOutline } from "@/skylticons"

const sharingCreateDoc = graphql(`
  mutation SharingCreate($id: String!, $data: String!) {
    sharingCreate(id: $id, data: $data) {
      success
    }
  }
`)

const sharingUpdateDoc = graphql(`
  mutation SharingUpdate($id: String!, $data: String!, $revision: Int!, $clear: Boolean) {
    sharingUpdate(id: $id, data: $data, revision: $revision, clear: $clear) {
      success
      stop
    }
  }
`)

const sharingDeleteDoc = graphql(`
  mutation SharingDelete($id: String!) {
    sharingDelete(id: $id) {
      success
    }
  }
`)

interface SharingProps {
  sign: Sign
  buttonClasses?: string
}

const Sharing: React.FC<SharingProps> = ({ sign, buttonClasses }) => {
  const [revision, setRevision] = useState(ClientData.sharingData?.revision || 0)
  const [active, setActive] = useState(ClientData.loadSign && ClientData.sharingData?.active ? true : false)
  const [showNotice, setShowNotice] = useState(false)
  const processUpdate = useRef<boolean>(true)

  const { t } = useTranslation()
  const iosInput = useRef(null)
  const id = ClientData.sharingData?.id
  const link = `${location.protocol}//${location.host}/shared_signs/${id}`

  const [createMutation] = useMutation(sharingCreateDoc)
  const [updateMutation] = useMutation(sharingUpdateDoc)
  const [deleteMutation] = useMutation(sharingDeleteDoc)

  const data = useCallback(() => {
    if (!sign.isLoaded) {
      return null
    }

    return JSON.stringify({ sign_type: sign.signType, fabric_json: sign.state })
  }, [sign])

  const copyLink = useCallback(() => {
    setShowNotice(true)
    copyToClipboard(link, iosInput.current)
    setTimeout(() => setShowNotice(false), 5000)
  }, [link, iosInput])

  const handleUpdate = useCallback(() => {
    if (!active || !id || !processUpdate.current) {
      return
    }

    const newData = data()
    if (!newData) {
      return
    }

    updateMutation({
      variables: { id, data: newData, revision },
      update: (_, { data }) => {
        if (data?.sharingUpdate) {
          if (data.sharingUpdate.stop) {
            setActive(false)
          } else {
            setRevision(revision + 1)
          }
        }
      },
    })
  }, [active, data, id, revision, updateMutation])

  const debouncedUpdate = useMemo(() => debounce(handleUpdate, 300, { leading: false }), [handleUpdate])

  const handleClear = useCallback(() => {
    debouncedUpdate.cancel()

    return new Promise<void>(resolve => {
      if (!active || !id) {
        return resolve()
      }

      updateMutation({
        variables: {
          id,
          data: "",
          revision,
          clear: true,
        },
        update: () => resolve(),
      })
    })
  }, [debouncedUpdate, active, id, updateMutation, revision])

  useEffect(() => {
    sign.onSignChanged.subscribe(debouncedUpdate)
    sign.onClear(handleClear)

    return () => {
      sign.onSignChanged.unsubscribe(debouncedUpdate)
      sign.onClearRemove(handleClear)
    }
  }, [debouncedUpdate, handleClear, sign])

  const handleStart = useCallback(() => {
    copyLink()

    if (env.TEST) {
      alert(link)
    }

    if (active || !id) {
      return
    }

    info(`creating share link ${link}`)

    const newData = data()
    if (!newData) {
      return
    }

    createMutation({
      variables: { id, data: newData },
      onCompleted: data => {
        if (data.sharingCreate?.success) {
          processUpdate.current = true
          setActive(true)
        }
      },
    })
  }, [active, copyLink, createMutation, data, id, link])

  const handleStop = useCallback(() => {
    if (!id) {
      return
    }

    processUpdate.current = false

    deleteMutation({
      variables: { id },
      update: (_, { data }) => {
        if (data?.sharingDelete?.success) {
          setActive(false)
          setShowNotice(false)
        }
      },
    })
  }, [id, deleteMutation])

  return (
    <div id="sign_share_link" className="sharing">
      <MediaQuery query={`(min-width: ${tailwindTheme.screensLg})`}>
        {() => (
          <RowButton
            id="sign_share_link"
            className={cn("start_link", { active }, buttonClasses)}
            onClick={active ? handleStop : () => handleStart()}
            iconButton={false}
            data-gtm="share"
          >
            <ShareOutline />
            <MediaQuery query={`(min-width: ${tailwindTheme.screensLg})`}>
              <span className="paragraph-xs mb-0">{active ? t("toolbox.stop_sharing") : t("toolbox.sharing")}</span>
            </MediaQuery>
            <MediaQuery query={`(max-width: ${tailwindTheme.screensMdmax})`}>
              <span>{active ? t("toolbox.stop_sharing") : t("toolbox.sharing")}</span>
            </MediaQuery>
          </RowButton>
        )}
      </MediaQuery>
      {showNotice && (
        <div className="notice z-40">
          {t("misc.link_copied")}
          {ios() && (
            <div className="ios_input">
              <input type="text" defaultValue={link} ref={iosInput} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Sharing
